import {
  IonButton,
  IonCol,
  IonGrid,
  IonInput,
  IonItem,
  IonRow,
  IonSegment,
  IonSegmentButton,
} from '@ionic/react';
import { isEmpty } from 'lodash';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect } from 'react-router';
import { QrCodeScannerButton } from '../../components/qr-code/qr-code-scanner-button';
import { ApplicationConfig, isLocalDev } from '../../config/config';
import { useBeaconRouter } from '../../route/route.hooks';
import { logger } from '../../util/logger';
import { DirectConnectGate } from '../gate/direct-connect-gate';
import { useDirectConnectHealthGateContext } from '../gate/direct-connect-health-gate';
import { isDirectConnectSupportedForPlatform } from '../helper/direct-connect-helper';

interface DirectConnectMachineAddProps {}

enum SegmentKey {
  SCAN = 'scan',
  MANUAL = 'manual',
}

interface DirectConnectMachineAddQrCodeProps {
  onAdd: (serialNumber: string) => void;
}

interface DirectConnectMachineAddManualProps {
  onAdd: (serialNumber: string) => void;
}

const DirectConnectMachineAdd = (
  _props: DirectConnectMachineAddProps
): ReactElement => {
  const { i18n } = useTranslation();

  const [selectedSegment, setSelectedSegment] = useState<string>(
    SegmentKey.SCAN
  );

  const [serialNumber, setSerialNumber] = useState<string | undefined>();

  const isSupported = isDirectConnectSupportedForPlatform();
  const isManualEnabled = useMemo(() => {
    return isLocalDev();
  }, []);

  // once we have a serial let's run our direct connect gate to manage
  // connecting to wifi and the robot. then we can cache the machine
  // and navigate to the machine page

  return (
    <>
      <IonSegment
        value={selectedSegment}
        onIonChange={(e) => {
          setSelectedSegment(e.detail.value as string);
        }}
        style={{
          height: '40px',
        }}
      >
        <IonSegmentButton value={SegmentKey.SCAN}>
          {i18n.t('common:scan')}
        </IonSegmentButton>
        {isManualEnabled && (
          <IonSegmentButton value={SegmentKey.MANUAL} disabled={!isSupported}>
            {i18n.t('common:manual')}
          </IonSegmentButton>
        )}
      </IonSegment>
      {selectedSegment === SegmentKey.SCAN && (
        <DirectConnectMachineAddQrCode onAdd={setSerialNumber} />
      )}
      {selectedSegment === SegmentKey.MANUAL && (
        <DirectConnectMachineAddManual onAdd={setSerialNumber} />
      )}
      {serialNumber && (
        <DirectConnectGate serialNumber={serialNumber}>
          <DirectConnectMachineRedirect />
        </DirectConnectGate>
      )}
    </>
  );
};

const DirectConnectMachineAddQrCode = (
  props: DirectConnectMachineAddQrCodeProps
) => {
  const { onAdd } = props;
  const { i18n } = useTranslation();

  const isScannerDisabled = useMemo(() => {
    return !isDirectConnectSupportedForPlatform();
  }, []);

  const handleScanData = useCallback(
    (data: string) => {
      let parsedData: { serialNumber: string } | undefined;
      try {
        parsedData = JSON.parse(data);
      } catch (error) {
        logger.error('Failed to parse the scanned data', { data });
        return;
      }

      if (parsedData?.serialNumber === undefined) {
        logger.error('Serial number not found in scanned data', {
          data,
        });
        return;
      }

      onAdd(parsedData.serialNumber);
    },
    [onAdd]
  );

  return (
    <IonGrid>
      <IonRow>
        <IonCol>
          {i18n.t('common:direct_connect_login_add_robot_instructions')}
        </IonCol>
      </IonRow>
      <IonRow>
        <IonCol>
          <QrCodeScannerButton
            handleScanData={handleScanData}
            isDisabled={isScannerDisabled}
            isValid={false}
          />
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};

const DirectConnectMachineAddManual = (
  props: DirectConnectMachineAddManualProps
) => {
  const { onAdd } = props;
  const { i18n } = useTranslation();

  const defaultSerialNumber = isLocalDev()
    ? ApplicationConfig.simulate.barcode.serialNumber
    : undefined;

  const [serialNumber, setSerialNumber] = useState<string | undefined>(
    defaultSerialNumber
  );

  const isSubmitEmpty = useMemo(() => {
    return isEmpty(serialNumber);
  }, [serialNumber]);

  return (
    <IonGrid>
      <IonRow>
        <IonCol>
          <IonItem className="transparent-background ion-margin" lines="full">
            <IonInput
              label={i18n.t('common:machine_serial_number')}
              labelPlacement="floating"
              onIonInput={(e) => {
                setSerialNumber(e.detail.value ?? undefined);
              }}
              value={serialNumber}
            />
          </IonItem>
        </IonCol>
      </IonRow>
      <IonRow>
        <IonCol className="ion-text-center">
          <IonButton
            disabled={isSubmitEmpty}
            onClick={() => {
              if (serialNumber) {
                onAdd(serialNumber);
              }
            }}
            style={{ width: '80%' }}
          >
            {i18n.t('common:add')}
          </IonButton>
        </IonCol>
      </IonRow>
    </IonGrid>
  );
};

const DirectConnectMachineRedirect = () => {
  const { healthStatus } = useDirectConnectHealthGateContext();
  const { builder } = useBeaconRouter();

  return (
    <Redirect
      to={builder.getMachineRoute(
        'unknown',
        healthStatus?.machine?.machineId as string
      )}
    />
  );
};

export { DirectConnectMachineAdd };
